import { api } from './client'

// Sales-assisted onboarding (activation) API. MOCK-ONLY — none of these
// endpoints exist on the real backend yet (nothing in serverapi tracking.md).
// The `/public/onboarding/*` calls are unauthenticated: the URL token is the
// credential, and the api client never redirects /public/* on 401.
//
// Model (2026-08-03 rework): the flow mirrors CCi's Zoho onboarding form
// ("eTX ImpaXt - Onboarding - New Shops") and collects NO payment. The buyer
// is a body shop (or MSO). Sales prices the deal as line items for context;
// invoicing happens outside this flow. Flow: shop info → systems → IT
// contact → sign the Data Authorization → book the installation call.
// No account/password here.

export type OnboardingStatus =
  | 'link_generated'
  | 'email_sent'
  | 'opened'
  | 'details_completed'
  | 'agreement_signed'
  | 'call_booked'
  | 'expired'

export interface LineItem {
  description: string
  monthlyAmountCents: number
  // 'platform' = the body shop's base subscription; 'dealership' = a connected
  // dealership added on top of the shop. Drives grouped display.
  kind?: 'platform' | 'dealership'
}

export type CciStatus = 'new' | 'existing' | 'unknown'

export type ShopType = 'single' | 'mso'

// Mirrors the Zoho form's intake. Sales prefills; the customer confirms.
export interface BusinessDetails {
  shopType?: ShopType
  legalBusinessName?: string // "Shop Name/MSO Name"
  street?: string
  city?: string
  state?: string // State/Region/Province
  zipCode?: string // Postal / Zip Code
  country?: string // 'United States' | 'Canada'
  shopPhone?: string
  shopEmail?: string
  billingEmail?: string // invoices go here
  primaryContactName?: string
  primaryContactEmail?: string
  bmsSystem?: string // Body Shop Management System
  estimatingSystems?: string[] // CCC / Mitchell / Audatex / Other (multi)
  estimatingOther?: string
  itContactFirstName?: string
  itContactLastName?: string
  itContactEmail?: string
  itContactPhone?: string
}

export interface BookingRecord {
  date: string
  time: string
  bookedAt: string
}

export interface ActivationView {
  status: OnboardingStatus
  shop: {
    name: string
    legalBusinessName: string
  }
  pricing: {
    lineItems: LineItem[]
    monthlyTotalCents: number
    currency: string
    contractTermMonths: number
  }
  agreement: {
    version: string
    title: string
    effectiveDate: string
    body: string[]
    signed: boolean
    signerName: string | null
  }
  businessDetails: BusinessDetails
  salesRep: { name: string; email: string; phone: string | null }
  support: { email: string; phone: string }
  booking: BookingRecord | null
  expiresAt: string
}

export interface BookingSlot {
  date: string
  label: string
  times: string[]
}

// -- Customer (unauthenticated, token-scoped) -------------------------------
export const activationApi = {
  get: (token: string) => api.get<ActivationView>(`/public/onboarding/${token}`),

  saveDetails: (token: string, payload: BusinessDetails) =>
    api.post<ActivationView>(`/public/onboarding/${token}/details`, payload),

  sign: (token: string, payload: { signerName: string; accepted: boolean }) =>
    api.post<ActivationView>(`/public/onboarding/${token}/sign`, payload),

  getSlots: (token: string) =>
    api.get<{ slots: BookingSlot[] }>(`/public/onboarding/${token}/slots`),

  book: (token: string, payload: { date: string; time: string }) =>
    api.post<ActivationView>(`/public/onboarding/${token}/book`, payload),

  // DEMO-ONLY: re-seeds the `demo` session so the flow can be walked again.
  // Real links are single-use.
  resetDemo: (token: string) =>
    api.post<ActivationView>(`/public/onboarding/${token}/_reset`),
}

// -- Sales / admin (authenticated) ------------------------------------------
export interface OnboardingLinkSummary {
  id: string
  shopName: string
  lineItems: LineItem[]
  monthlyTotalCents: number
  contractTermMonths: number
  contactEmail: string
  cciStatus: CciStatus
  salesRepName: string
  status: OnboardingStatus
  createdAt: string
  expiresAt: string
  openedAt: string | null
  detailsAt: string | null
  signedAt: string | null
  bookedAt: string | null
  booking: BookingRecord | null
  audit: Array<{ event: string; actor: string; at: string; ip?: string }>
}

export interface GenerateLinkPayload {
  shopName: string
  legalBusinessName?: string
  contactEmail: string
  signerName?: string
  lineItems: LineItem[]
  contractTermMonths: number
  prefill?: BusinessDetails
  cciStatus?: CciStatus
  salesRepName: string
  salesRepEmail: string
  salesRepPhone?: string
}

export const onboardingLinksApi = {
  list: () => api.get<{ sessions: OnboardingLinkSummary[] }>(`/onboarding-links`),

  generate: (payload: GenerateLinkPayload) =>
    api.post<{ id: string; activationUrl: string; rawToken: string }>(
      `/onboarding-links`,
      payload,
    ),

  resend: (id: string) =>
    api.post<OnboardingLinkSummary>(`/onboarding-links/${id}/resend`),
}

// Shared display helper.
export function formatMoney(cents: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100)
}
